import { useEffect, useState } from "react";
import { Loader2, Eye, Users, Smartphone, Monitor } from "lucide-react";
import { api } from "@/lib/api";
import { SEO } from "@/seo/useSeo";

const RANGES = [["today", "Today"], ["7d", "7 days"], ["30d", "30 days"], ["all", "All time"]];
const PAGE_NAMES = Object.fromEntries([["/", "Home"], ...SEO.site.nav, ...SEO.site.industries]);
const DEVICE = { phone: ["Phone / tablet", Smartphone], computer: ["Computer", Monitor] };

const Tile = ({ icon: Icon, label, value, hint, testId }) => (
  <div className="bg-card border border-border rounded-xl p-4" data-testid={testId}>
    <p className="text-xs text-muted-foreground inline-flex items-center gap-1.5"><Icon className="w-3.5 h-3.5" /> {label}</p>
    <p className="font-display text-3xl font-bold mt-1 tabular-nums">{value}</p>
    {hint && <p className="text-[11px] text-muted-foreground mt-0.5">{hint}</p>}
  </div>
);

const BarList = ({ title, rows, name, testId }) => {
  const max = Math.max(1, ...rows.map((r) => r.visits));
  return (
    <div className="bg-card border border-border rounded-xl p-4" data-testid={testId}>
      <div className="flex items-center justify-between mb-3 text-xs text-muted-foreground">
        <p className="text-sm font-semibold text-foreground">{title}</p><span>visits · visitors</span>
      </div>
      {rows.length === 0 ? <p className="text-sm text-muted-foreground py-4">No visits yet.</p> : (
        <div className="space-y-2.5">
          {rows.map((r) => (
            <div key={r.key} data-testid={`${testId}-row-${r.key}`}>
              <div className="flex items-center justify-between gap-3 text-sm">
                <span className="truncate">{name(r.key)}</span>
                <span className="font-mono text-xs shrink-0"><b>{r.visits}</b> · <span className="text-muted-foreground">{r.visitors}</span></span>
              </div>
              <div className="h-1.5 mt-1 rounded-full bg-secondary overflow-hidden"><div className="h-full rounded-full bg-primary" style={{ width: `${(r.visits / max) * 100}%` }} /></div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const VisitsPanel = () => {
  const [range, setRange] = useState("30d");
  const [data, setData] = useState(null);
  useEffect(() => { setData(null); api.ownerVisits(range).then(setData).catch(() => setData({ error: true })); }, [range]);
  const pct = (k) => (data?.total?.visits ? Math.round(((data.devices.find((d) => d.key === k)?.visits || 0) / data.total.visits) * 100) : 0);
  return (
    <div className="space-y-4" data-testid="owner-visits">
      <div className="inline-flex rounded-full border border-border bg-card p-1" role="tablist">
        {RANGES.map(([k, l]) => (
          <button key={k} type="button" onClick={() => setRange(k)} data-testid={`visits-range-${k}`}
            className={`px-3.5 py-1.5 text-sm rounded-full transition-colors ${range === k ? "bg-primary text-white" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
        ))}
      </div>
      {!data ? <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        : data.error ? <p className="text-sm text-destructive" data-testid="visits-error">Could not load visits.</p> : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <Tile icon={Eye} label="Visits" value={data.total.visits} testId="visits-total" />
              <Tile icon={Users} label="Unique visitors" value={data.total.visitors} hint="each person once per day" testId="visits-unique" />
              <Tile icon={Smartphone} label="On phones" value={`${pct("phone")}%`} testId="visits-phone-pct" />
              <Tile icon={Monitor} label="On computers" value={`${pct("computer")}%`} testId="visits-computer-pct" />
            </div>
            <div className="grid lg:grid-cols-3 gap-3">
              <BarList title="Visits per page" rows={data.pages} name={(k) => PAGE_NAMES[k] || k} testId="visits-pages" />
              <BarList title="Where visitors came from" rows={data.sources} name={(k) => k} testId="visits-sources" />
              <BarList title="Phone vs computer" rows={data.devices} name={(k) => DEVICE[k]?.[0] || k} testId="visits-devices" />
            </div>
          </>
        )}
      <p className="text-xs text-muted-foreground" data-testid="visits-note">Counts your public pages only (home and landing pages). Sign-in, dashboards and anyone signed in are never counted. A person counts as one visitor per day; no names, emails or IP addresses are stored.</p>
    </div>
  );
};
